import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Link2,
  Loader2,
  MessageSquare,
  Plus,
  RotateCcw,
  Search,
  Send,
  X,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useAdminCreatorRequest,
  useUpdateCreatorRequest,
  useAddCreatorRequestComment,
  useAdminInvites,
  useAdminNiches,
  useAdminTeams,
  useAllAdminInviters,
  useAdminAttachInviteToRequest,
  useAdminDetachInviteFromRequest,
} from '@/hooks/useAdmin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Pill, toneFor } from '@/components/ui/pill';
import { Tag, channelToneFor, channelLabel } from '@/components/ui/tag';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PlatformIcon } from '@/components/PlatformIcon';
import { InviteStagePill } from '@/components/invites/InviteStagePill';
import { firstNameOf } from '@/lib/displayName';
import {
  CANCELLATION_REASON_LABELS,
  cancellationReasonLabel,
  creatorTypeLabel,
  requestStatusLabel,
  teamLabel,
} from '@/lib/labels';
import { daysSince, formatDate, formatDateTime } from '@/lib/dates';
import { completedByLabel, formatDayCount } from '@/lib/peoplePages';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';
import type {
  CreatorRequestCancellationReason,
  CreatorRequestDetail,
  CreatorRequestStatus,
  InviteStatus,
} from '@/api/types';
import { inviteLink } from '@/lib/moneyPages';

const FUNNEL = (
  ['REQUESTED', 'IN_PROGRESS', 'AWAITING_RESPONSE', 'AWAITING_RATE', 'ROSTERED'] as const
).map((value) => ({ value, label: requestStatusLabel(value) }));

type FunnelStatus = (typeof FUNNEL)[number]['value'];

const STEP_TONE: Record<FunnelStatus, 'warning' | 'info' | 'success'> = {
  REQUESTED: 'warning',
  IN_PROGRESS: 'info',
  AWAITING_RESPONSE: 'info',
  AWAITING_RATE: 'warning',
  ROSTERED: 'success',
};

const CANCELLATION_REASONS = Object.keys(
  CANCELLATION_REASON_LABELS,
) as CreatorRequestCancellationReason[];

/**
 * Batch items carry who checked them off. Not on the generated type, so read
 * it defensively; when absent the banner stays neutral.
 */
type DetailWithCheckOff = CreatorRequestDetail & {
  checkedOffAt?: string | null;
  checkedOffByInviterId?: number | null;
};

const COMMENT_ROLE_LABEL: Record<'admin' | 'team' | 'cm', string> = {
  admin: 'Admin',
  team: 'Outreacher',
  cm: 'Campaign manager',
};

export function AdminCreatorRequestDetail(): JSX.Element {
  const { publicId } = useParams<{ publicId: string }>();
  const query = useAdminCreatorRequest(publicId ?? null);
  const update = useUpdateCreatorRequest();
  const addComment = useAddCreatorRequestComment();
  const teams = useAdminTeams();
  const niches = useAdminNiches();

  const [commentBody, setCommentBody] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState<
    CreatorRequestCancellationReason | ''
  >('');
  const [cancellationNote, setCancellationNote] = useState('');

  const request = (query.data ?? null) as DetailWithCheckOff | null;

  // Every inviter (not just the first page) so assignee names always resolve.
  const inviters = useAllAdminInviters({}, { enabled: !!request });

  const teamInviters = useMemo(
    () =>
      (inviters.data ?? []).filter(
        (i) => request && i.team === request.targetTeam && (i.isActive || i.id === request.assignedInviterId),
      ),
    [inviters.data, request],
  );
  const inviterName = (id: number | null | undefined): string | null =>
    id ? (inviters.data ?? []).find((i) => i.id === id)?.name ?? null : null;

  const teamName = useMemo(() => {
    if (!request) return '';
    const t = (teams.data ?? []).find((row) => row.code === request.targetTeam);
    return teamLabel(request.targetTeam, t?.displayName);
  }, [teams.data, request]);

  const nicheName = (code: string): string =>
    (niches.data ?? []).find((n) => n.code === code)?.displayName ?? code;

  async function moveTo(next: CreatorRequestStatus): Promise<void> {
    if (!request || next === request.status) return;
    try {
      await update.mutateAsync({ publicId: request.publicId, body: { status: next } });
      toast.success(`Moved to ${requestStatusLabel(next)}`);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function markComplete(): Promise<void> {
    if (!request) return;
    try {
      await update.mutateAsync({ publicId: request.publicId, body: { status: 'COMPLETED' } });
      toast.success('Marked complete');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function reopen(): Promise<void> {
    if (!request) return;
    try {
      await update.mutateAsync({ publicId: request.publicId, body: { status: 'REQUESTED' } });
      toast.success('Reopened');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function confirmCancel(): Promise<void> {
    if (!request) return;
    if (!cancellationReason) {
      toast.error('Pick a reason first');
      return;
    }
    try {
      await update.mutateAsync({
        publicId: request.publicId,
        body: {
          status: 'CANCELLED',
          cancellationReason: cancellationReason || null,
          cancellationNote: cancellationNote.trim() || null,
        },
      });
      toast.success('Request cancelled');
      setCancelOpen(false);
      setCancellationReason('');
      setCancellationNote('');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function assignInviter(inviterIdStr: string): Promise<void> {
    if (!request) return;
    const id = inviterIdStr ? Number.parseInt(inviterIdStr, 10) : null;
    try {
      await update.mutateAsync({ publicId: request.publicId, body: { assignedInviterId: id } });
      toast.success(id ? `Assigned to ${inviterName(id) ?? 'outreacher'}` : 'Unassigned');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function postComment(): Promise<void> {
    if (!request || !commentBody.trim()) return;
    try {
      await addComment.mutateAsync({ publicId: request.publicId, body: commentBody.trim() });
      setCommentBody('');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  const back = (
    <Link
      to="/admin/creator-requests"
      className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-3 w-3" /> Creator requests
    </Link>
  );

  if (query.isLoading) {
    return (
      <div className="space-y-5">
        {back}
        <div className="flex items-center text-sm text-muted-foreground" aria-live="polite">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading request…
        </div>
      </div>
    );
  }
  if (query.error || !request) {
    return (
      <div className="space-y-5">
        {back}
        <p role="alert" className="text-sm text-destructive">
          {query.error ? `Couldn't load this request: ${(query.error as Error).message}` : 'Request not found.'}
        </p>
      </div>
    );
  }

  const isCompleted = request.status === 'COMPLETED';
  const isCancelled = request.status === 'CANCELLED';
  const isClosed = isCompleted || isCancelled;
  const requester = firstNameOf(request.requesterName) || request.requesterName;
  const title =
    request.requestType === 'CREATOR'
      ? request.targetIdentifier ?? 'Creator request'
      : request.niches.length > 0
        ? request.niches.map(nicheName).join(' + ')
        : 'Niche request';
  const completedBy = inviterName(request.checkedOffByInviterId);

  return (
    <div className="space-y-5">
      {back}

      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <Pill tone={toneFor(request.status)}>{requestStatusLabel(request.status)}</Pill>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {request.requestType === 'NICHE' ? <Tag tone="niche">Niche</Tag> : <Tag tone="creator">Creator</Tag>}
          <Tag>{teamName}</Tag>
          {request.channel && (
            <Tag tone={channelToneFor(request.channel)}>{channelLabel(request.channel)}</Tag>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <span>
            From campaign manager{' '}
            <span className="font-medium text-foreground/80">{requester}</span>{' '}
            <span className="text-muted-foreground/80">({request.requesterEmail})</span>
          </span>
          <span aria-hidden className="text-muted-foreground/50">·</span>
          <span title={formatDateTime(request.createdAt)}>
            Requested {formatDate(request.createdAt)} ({formatDayCount(daysSince(request.createdAt)).toLowerCase()}
            {daysSince(request.createdAt) ? ' ago' : ''})
          </span>
          <span aria-hidden className="text-muted-foreground/50">·</span>
          <span title={formatDateTime(request.updatedAt)}>Last activity {formatDate(request.updatedAt)}</span>
        </div>
      </header>

      {isCompleted && (
        <ClosedBanner
          tone="success"
          title={completedByLabel(completedBy)}
          body={
            request.checkedOffAt
              ? `Finished ${formatDate(request.checkedOffAt)}.`
              : 'This request is finished.'
          }
          onReopen={reopen}
          reopening={update.isPending}
        />
      )}
      {isCancelled && (
        <ClosedBanner
          tone="destructive"
          title={`Cancelled · ${request.cancellationReason ? cancellationReasonLabel(request.cancellationReason) : 'no reason given'}`}
          body={request.cancellationNote ?? null}
          onReopen={reopen}
          reopening={update.isPending}
        />
      )}

      {!isClosed && (
        <StatusFlow
          current={request.status}
          onMove={moveTo}
          onComplete={markComplete}
          onCancelStart={() => setCancelOpen((v) => !v)}
          disabled={update.isPending}
          cancelOpen={cancelOpen}
        />
      )}

      {cancelOpen && !isClosed && (
        <Card className="border-destructive/30">
          <CardContent className="space-y-3 pt-6">
            <div className="grid gap-3 sm:grid-cols-[1fr_2fr] sm:items-start">
              <div className="space-y-1.5">
                <Label className="text-xs">Reason</Label>
                <Combobox
                  value={cancellationReason}
                  onChange={(v) => setCancellationReason(v as CreatorRequestCancellationReason)}
                  options={CANCELLATION_REASONS.map((r) => ({ value: r, label: cancellationReasonLabel(r) }))}
                  placeholder="Pick a reason"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cancel-note" className="text-xs">
                  Note <span className="text-muted-foreground">(optional, visible to the campaign manager)</span>
                </Label>
                <Input
                  id="cancel-note"
                  placeholder="e.g. retry next quarter once budget refreshes"
                  value={cancellationNote}
                  onChange={(e) => setCancellationNote(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setCancelOpen(false);
                  setCancellationReason('');
                  setCancellationNote('');
                }}
              >
                Keep open
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={confirmCancel}
                disabled={update.isPending || !cancellationReason}
              >
                {update.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                Cancel request
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-5">
          <AttachedInvites request={request} isClosed={isClosed} />

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                Conversation
                <span className="text-xs font-normal text-muted-foreground">with {requester}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {request.comments.length === 0 ? (
                <EmptyHint
                  icon={<MessageSquare className="h-4 w-4" />}
                  primary="No messages yet"
                  secondary="Leave a note so the campaign manager knows where things stand."
                />
              ) : (
                <ul className="space-y-3">
                  {request.comments.map((c) => {
                    const mine = c.authorSide === 'OUTREACH';
                    const role = c.authorRole === 'admin' ? 'admin' : mine ? 'team' : 'cm';
                    return (
                      <li
                        key={c.id}
                        className={cn(
                          'rounded-md px-3 py-2.5',
                          mine ? 'bg-brand/10 ring-1 ring-brand/15' : 'bg-muted/40',
                        )}
                      >
                        <div className="mb-1 flex items-baseline justify-between gap-3">
                          <div className="flex items-baseline gap-2">
                            <span className="text-sm font-medium">
                              {firstNameOf(c.authorName) || c.authorName}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {COMMENT_ROLE_LABEL[role]}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">{formatDateTime(c.createdAt)}</span>
                        </div>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{c.body}</p>
                      </li>
                    );
                  })}
                </ul>
              )}

              {!isClosed && (
                <div className="space-y-2 border-t border-border/40 pt-4">
                  <Label htmlFor="reply" className="sr-only">
                    Reply to the campaign manager
                  </Label>
                  <textarea
                    id="reply"
                    className="min-h-[88px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    value={commentBody}
                    onChange={(e) => setCommentBody(e.target.value)}
                    placeholder="Reply to the campaign manager…"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        void postComment();
                      }
                    }}
                  />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>⌘+Enter to send</span>
                    <Button size="sm" onClick={postComment} disabled={addComment.isPending || !commentBody.trim()}>
                      {addComment.isPending ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <Send className="mr-1 h-3 w-3" />
                      )}
                      Post
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <aside className="space-y-5">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Brief</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pb-5">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
                <BriefRow label="Type">{request.requestType === 'NICHE' ? 'Niche' : 'Creator'}</BriefRow>
                {request.niches.length > 0 && (
                  <BriefRow label={request.niches.length > 1 ? 'Niches' : 'Niche'}>
                    <div className="flex flex-wrap gap-1.5">
                      {request.niches.map((n) => (
                        <Tag key={n} tone="niche">
                          {nicheName(n)}
                        </Tag>
                      ))}
                    </div>
                  </BriefRow>
                )}
                {request.minFollowers != null && (
                  <BriefRow label="Min followers">{request.minFollowers.toLocaleString('en-US')}+</BriefRow>
                )}
                {request.categoryHint && (
                  <BriefRow label="Creator type">{creatorTypeLabel(request.categoryHint)}</BriefRow>
                )}
              </dl>
              {request.brief?.trim() ? (
                <blockquote className="rounded-md bg-muted/40 px-3 py-2.5 text-sm leading-relaxed text-foreground/90">
                  <p className="whitespace-pre-wrap">{request.brief}</p>
                </blockquote>
              ) : (
                <p className="text-xs italic text-muted-foreground">
                  No brief — the campaign manager left it blank.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Assignment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 pb-5">
              <Label className="text-xs text-muted-foreground">Assigned outreacher</Label>
              <Combobox
                value={request.assignedInviterId ? String(request.assignedInviterId) : ''}
                onChange={(v) => void assignInviter(v)}
                loading={inviters.isLoading}
                options={teamInviters.map((i) => ({
                  value: String(i.id),
                  label: i.isActive ? i.name : `${i.name} (inactive)`,
                }))}
                allowAll
                allLabel="Unassigned"
                placeholder={`Pick from ${teamName}`}
                disabled={update.isPending}
              />
              <p className="text-xs text-muted-foreground">Active outreachers on {teamName}.</p>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

// ── Attached invites ──────────────────────────────────────────────────────

function AttachedInvites({ request, isClosed }: { request: CreatorRequestDetail; isClosed: boolean }): JSX.Element {
  const attach = useAdminAttachInviteToRequest();
  const detach = useAdminDetachInviteFromRequest();
  const [panelOpen, setPanelOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search.trim(), 300);
  const attached = request.attachedInvites ?? [];
  const attachedIds = new Set(attached.map((a) => a.inviteId));
  const canAttach = !!request.assignedInviterId;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Link2 className="h-4 w-4 text-muted-foreground" />
          Attached invites
          <span className="text-xs font-normal text-muted-foreground">{attached.length}</span>
        </CardTitle>
        {!panelOpen && !isClosed && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPanelOpen(true)}
            disabled={!canAttach}
            title={canAttach ? 'Attach an invite that was already logged' : 'Assign an outreacher first'}
          >
            <Plus className="mr-1 h-3 w-3" /> Attach
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {!canAttach && !isClosed && (
          <p className="text-xs text-muted-foreground">
            Assign an outreacher before attaching or detaching invites.
          </p>
        )}
        {attached.length === 0 ? (
          <EmptyHint
            icon={<Link2 className="h-4 w-4" />}
            primary="No invites attached yet"
            secondary={
              isClosed
                ? 'This request is finished.'
                : 'Invites logged against this request show here. You can also attach one that was logged already.'
            }
          />
        ) : (
          <ul className="divide-y divide-border/40 overflow-hidden rounded-md border border-border/40">
            {attached.map((a) => (
              <li
                key={a.attachmentId}
                className="flex items-center justify-between gap-2 px-3 py-2 text-sm transition-colors hover:bg-muted/40"
              >
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
                  <PlatformIcon platform={a.channel} size={14} className="text-muted-foreground" />
                  <Link
                    to={inviteLink({ handle: a.targetIdentifier, openPublicId: a.invitePublicId })}
                    className="truncate font-medium text-foreground hover:underline"
                  >
                    @{a.targetIdentifier}
                  </Link>
                  <InviteStagePill invite={{ status: a.inviteStatus as InviteStatus }} />
                  {a.isRoster && <Tag tone="neutral">Roster</Tag>}
                  <span className="text-xs text-muted-foreground">Attached {formatDate(a.attachedAt)}</span>
                </div>
                {!isClosed && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-muted-foreground hover:text-destructive"
                    disabled={detach.isPending || !canAttach}
                    aria-label={`Detach @${a.targetIdentifier}`}
                    title="Detach from this request (the invite itself is kept)"
                    onClick={() =>
                      detach.mutate(
                        { publicId: request.publicId, inviteId: a.inviteId },
                        {
                          onSuccess: () => toast.success(`Detached @${a.targetIdentifier}`),
                          onError: (err: Error) => toast.error(err.message),
                        },
                      )
                    }
                  >
                    <X className="h-3.5 w-3.5" /> Detach
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {panelOpen && !isClosed && (
          <AttachPanel
            request={request}
            search={search}
            debounced={debounced}
            onSearch={setSearch}
            excludeIds={attachedIds}
            attaching={attach.isPending}
            onAttach={(inviteId, handle) =>
              attach.mutate(
                { publicId: request.publicId, inviteId },
                {
                  onSuccess: () => {
                    toast.success(`Attached @${handle}`);
                    setSearch('');
                  },
                  onError: (err: Error) => toast.error(err.message),
                },
              )
            }
            onDone={() => {
              setPanelOpen(false);
              setSearch('');
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function AttachPanel({
  request,
  search,
  debounced,
  onSearch,
  excludeIds,
  attaching,
  onAttach,
  onDone,
}: {
  request: CreatorRequestDetail;
  search: string;
  debounced: string;
  onSearch: (v: string) => void;
  excludeIds: Set<number>;
  attaching: boolean;
  onAttach: (inviteId: number, handle: string) => void;
  onDone: () => void;
}): JSX.Element {
  // Invites on the request's team, newest first; typing narrows by handle.
  const results = useAdminInvites({
    team: request.targetTeam,
    ...(request.channel ? { channel: request.channel } : {}),
    ...(debounced ? { search: debounced } : {}),
    limit: 10,
  });
  const rows = (results.data?.data ?? []).filter((i) => !excludeIds.has(i.id) && !i.deletedAt);

  return (
    <div className="space-y-2 rounded-md border border-border/60 bg-muted/20 p-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          aria-label="Search invites by handle"
          placeholder="Search by handle (showing the newest on this team)"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          className="pl-8"
        />
      </div>
      <ul className="max-h-56 space-y-1 overflow-y-auto" aria-live="polite">
        {results.isLoading ? (
          <li className="flex items-center gap-2 px-1 py-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading
          </li>
        ) : results.error ? (
          <li className="px-1 py-2 text-xs text-destructive">{(results.error as Error).message}</li>
        ) : rows.length === 0 ? (
          <li className="px-1 py-2 text-xs text-muted-foreground">
            {debounced ? 'No invites on this team match that handle.' : 'No invites on this team to attach.'}
          </li>
        ) : (
          rows.map((inv) => (
            <li
              key={inv.id}
              className="flex items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-background"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <PlatformIcon platform={inv.channel} size={14} className="text-muted-foreground" />
                <span className="truncate font-medium">@{inv.targetIdentifier}</span>
                <InviteStagePill invite={inv} />
                <span className="text-xs text-muted-foreground">{formatDate(inv.createdAt)}</span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-7"
                disabled={attaching}
                onClick={() => onAttach(inv.id, inv.targetIdentifier)}
              >
                Attach
              </Button>
            </li>
          ))
        )}
      </ul>
      <div className="flex justify-end">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

// ── Status flow ───────────────────────────────────────────────────────────

function StatusFlow({
  current,
  onMove,
  onComplete,
  onCancelStart,
  disabled,
  cancelOpen,
}: {
  current: CreatorRequestStatus;
  onMove: (s: CreatorRequestStatus) => void;
  onComplete: () => void;
  onCancelStart: () => void;
  disabled: boolean;
  cancelOpen: boolean;
}): JSX.Element {
  const currentIndex = FUNNEL.findIndex((s) => s.value === current);
  return (
    <Card>
      <CardContent className="space-y-4 pb-5 pt-5">
        <ol className="flex w-full flex-wrap items-center gap-y-2" aria-label="Request progress">
          {FUNNEL.map((step, i) => {
            const state: 'past' | 'current' | 'next' | 'future' =
              i < currentIndex ? 'past' : i === currentIndex ? 'current' : i === currentIndex + 1 ? 'next' : 'future';
            const tone = STEP_TONE[step.value];
            return (
              <li key={step.value} className={cn('flex items-center', i < FUNNEL.length - 1 && 'flex-1')}>
                <button
                  type="button"
                  disabled={disabled || state === 'current'}
                  onClick={() => onMove(step.value)}
                  aria-current={state === 'current' ? 'step' : undefined}
                  aria-label={state === 'next' ? `Move to ${step.label} (next step)` : `Move to ${step.label}`}
                  className={cn(
                    'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1 text-xs font-medium transition-all duration-150',
                    state === 'current' && [
                      'font-semibold ring-2 ring-offset-2 ring-offset-card',
                      tone === 'warning' && 'bg-warning/25 text-pending-strong ring-warning/40',
                      tone === 'info' && 'bg-info/25 text-info-strong ring-info/40',
                      tone === 'success' && 'bg-success/25 text-success-strong ring-success/40',
                    ],
                    state === 'past' && [
                      'opacity-85 hover:opacity-100',
                      tone === 'warning' && 'bg-warning/15 text-pending-strong',
                      tone === 'info' && 'bg-info/15 text-info-strong',
                      tone === 'success' && 'bg-success/15 text-success-strong',
                    ],
                    state === 'next' &&
                      'border-2 border-brand/60 bg-brand/5 text-foreground/85 hover:border-primary hover:bg-brand/10',
                    state === 'future' &&
                      'border border-border bg-transparent text-muted-foreground hover:border-foreground/40 hover:text-foreground/70',
                    state !== 'current' && !disabled && 'cursor-pointer',
                    disabled && 'cursor-wait opacity-50',
                  )}
                >
                  {state === 'current' && (
                    <span
                      aria-hidden
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        tone === 'warning' && 'bg-warning',
                        tone === 'info' && 'bg-info',
                        tone === 'success' && 'bg-success',
                      )}
                    />
                  )}
                  {step.label}
                </button>
                {i < FUNNEL.length - 1 && (
                  <span
                    aria-hidden
                    className={cn(
                      'mx-1.5 h-px flex-1 transition-colors duration-150',
                      i < currentIndex ? 'bg-brand/40' : 'bg-border',
                    )}
                  />
                )}
              </li>
            );
          })}
        </ol>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/40 pt-3">
          <p className="text-xs text-muted-foreground">
            Click any step to move there. Mark complete or cancel when you're done.
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onComplete}
              disabled={disabled}
              className="border-success/40 text-success-strong hover:bg-success/10"
            >
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
              Mark complete
            </Button>
            <Button
              size="sm"
              variant={cancelOpen ? 'destructive' : 'outline'}
              onClick={onCancelStart}
              disabled={disabled}
              aria-expanded={cancelOpen}
              className={cn(
                !cancelOpen && 'border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive',
              )}
            >
              <XCircle className="mr-1.5 h-3.5 w-3.5" />
              {cancelOpen ? 'Pick a reason…' : 'Cancel request'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BriefRow({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <>
      <dt className="self-center text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-foreground">{children}</dd>
    </>
  );
}

function EmptyHint({
  icon,
  primary,
  secondary,
}: {
  icon: ReactNode;
  primary: string;
  secondary: string;
}): JSX.Element {
  return (
    <div className="flex items-start gap-3 rounded-md border border-dashed border-border/60 bg-muted/20 px-3 py-3">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="space-y-0.5">
        <p className="text-sm font-medium text-foreground">{primary}</p>
        <p className="text-xs text-muted-foreground">{secondary}</p>
      </div>
    </div>
  );
}

function ClosedBanner({
  tone,
  title,
  body,
  onReopen,
  reopening,
}: {
  tone: 'success' | 'destructive';
  title: string;
  body: string | null;
  onReopen: () => void;
  reopening: boolean;
}): JSX.Element {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-md border px-4 py-3',
        tone === 'success' ? 'border-success/30 bg-success/5' : 'border-destructive/30 bg-destructive/5',
      )}
    >
      <ChevronRight
        className={cn('mt-0.5 h-4 w-4 shrink-0', tone === 'success' ? 'text-success-strong' : 'text-destructive')}
      />
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className={cn('text-sm font-medium', tone === 'success' ? 'text-success-strong' : 'text-destructive')}>
          {title}
        </p>
        {body && <p className="text-sm text-foreground/80">{body}</p>}
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={onReopen}
        disabled={reopening}
        className="shrink-0 text-xs text-muted-foreground hover:text-foreground"
      >
        {reopening ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <RotateCcw className="mr-1.5 h-3 w-3" />}
        Reopen
      </Button>
    </div>
  );
}
